// Email content-builder domain model (EMAIL-005 / EMAIL-007 / EMAIL-008 / EMAIL-009).
//
// Canonical 20-block model transcribed from the Step 3 screenshot set
// (`conent builder blocks/` settings composites + `block preview images/`).
// Block type keys are the camelCase of the canonical palette labels from
// SCREENSHOT_SOURCE_MAP.md; every settings field maps 1:1 to a labelled
// control in the block's settings screenshot (label noted in a comment).
//
// Stored state is `builderStateJson` version 2. Version 1 payloads are still
// parsed and migrated best-effort (see parseEmailBuilderStateJson).

import advertisingPdfPreview from "../../../images/email-builder/advertising-pdf.png";
import advocacyUpdateBannerPreview from "../../../images/email-builder/advocacy-update-banner.png";
import alertBannerPreview from "../../../images/email-builder/alert-banner.png";
import articleListPreview from "../../../images/email-builder/article-list.png";
import emailBannerPreview from "../../../images/email-builder/email-banner.png";
import eventAnnouncementPreview from "../../../images/email-builder/event-announcement.png";
import fourGridIconAndTitlePreview from "../../../images/email-builder/four-grid-icon-and-title.png";
import inquiriesPreview from "../../../images/email-builder/inquiries.png";
import keyLinksPreview from "../../../images/email-builder/key-links.png";
import memberSpotlightPreview from "../../../images/email-builder/member-spotlight.png";
import signaturePreview from "../../../images/email-builder/signature.png";
import sixGridIconAndTitlePreview from "../../../images/email-builder/six-grid-icon-and-title.png";
import splitArticleWithBorderPreview from "../../../images/email-builder/split-article-with-border.png";
import specialGuestSponsorPreview from "../../../images/email-builder/sponsor-block-margin.png";
import sponsorsListPreview from "../../../images/email-builder/sponsors-list-1-to-3-sponsors-per-block.png";
import textAndButtonHeroBannerPreview from "../../../images/email-builder/text-and-button-hero-banner.png";
import textAndEmailContentPreview from "../../../images/email-builder/text-and-email-content.png";
import textHeroBannerPreview from "../../../images/email-builder/text-hero-banner.png";
import upcomingEventsListPreview from "../../../images/email-builder/upcoming-events-list.png";
import updateBannerPreview from "../../../images/email-builder/update-banner.png";

// ---------------------------------------------------------------------------
// Shared colors / icons
// ---------------------------------------------------------------------------

export const emailBuilderColors = {
  darkNavy: "#0B1F33",
  blue: "#3B82F6",
  lightBlue: "#EAF3FE",
  veryLightGray: "#F8F9FB",
  white: "#FFFFFF",
  black: "#050505",
} as const;

export type EmailBuilderIconKey =
  | "calendar"
  | "megaphone"
  | "memberSearch"
  | "personAdd"
  | "people"
  | "briefcase"
  | "bank"
  | "phone"
  | "tag"
  | "pencil"
  | "warning"
  | "sparkle"
  | "bolt"
  | "dollar"
  | "gavel"
  | "email"
  | "clock"
  | "mapPin"
  | "link";

// Conservative glyphs used inside colored icon tiles when rendering email
// HTML (email clients cannot load icon fonts reliably).
export const emailBuilderIconGlyphs: Record<EmailBuilderIconKey, string> = {
  calendar: "&#128197;",
  megaphone: "&#128227;",
  memberSearch: "&#128269;",
  personAdd: "&#128100;",
  people: "&#128101;",
  briefcase: "&#128188;",
  bank: "&#127963;",
  phone: "&#128241;",
  tag: "&#127991;",
  pencil: "&#9997;",
  warning: "&#9888;",
  sparkle: "&#10024;",
  bolt: "&#9889;",
  dollar: "&#128176;",
  gavel: "&#128296;",
  email: "&#9993;",
  clock: "&#128336;",
  mapPin: "&#128205;",
  link: "&#128279;",
};

// Options for the gold "Change Icon" pickers in block settings panels.
export const emailBuilderIconOptions: Array<{
  key: EmailBuilderIconKey;
  label: string;
}> = [
  { key: "calendar", label: "Calendar" },
  { key: "megaphone", label: "Megaphone" },
  { key: "memberSearch", label: "Member Search" },
  { key: "personAdd", label: "Person Add" },
  { key: "people", label: "People" },
  { key: "briefcase", label: "Briefcase" },
  { key: "bank", label: "Bank" },
  { key: "phone", label: "Phone" },
  { key: "tag", label: "Tag" },
  { key: "pencil", label: "Pencil" },
  { key: "warning", label: "Warning" },
  { key: "sparkle", label: "Sparkle" },
  { key: "bolt", label: "Lightning Bolt" },
  { key: "dollar", label: "Dollar" },
  { key: "gavel", label: "Gavel" },
  { key: "email", label: "Email" },
  { key: "clock", label: "Clock" },
  { key: "mapPin", label: "Map Pin" },
  { key: "link", label: "Link" },
];

// ---------------------------------------------------------------------------
// Block type keys (canonical 20)
// ---------------------------------------------------------------------------

export type EmailBuilderBlockType =
  | "textAndButtonHeroBanner"
  | "textHeroBanner"
  | "emailBanner"
  | "updateBanner"
  | "alertBanner"
  | "advocacyUpdateBanner"
  | "memberSpotlight"
  | "articleList"
  | "splitArticleWithBorder"
  | "textAndEmailContent"
  | "fourGridIconAndTitle"
  | "sixGridIconAndTitle"
  | "upcomingEventsList"
  | "eventAnnouncement"
  | "specialGuestSponsor"
  | "sponsorsList"
  | "advertisingPdf"
  | "keyLinks"
  | "inquiries"
  | "signature";

// Canonical palette labels (also used by the selected-block "Block | <name>"
// chip — the real block name, never the mock's copy-pasted chip text).
export const emailBuilderBlockLabels: Record<EmailBuilderBlockType, string> = {
  textAndButtonHeroBanner: "Text and Button Hero Banner",
  textHeroBanner: "Text Hero Banner",
  emailBanner: "Email Banner",
  updateBanner: "Update Banner",
  alertBanner: "Alert Banner",
  advocacyUpdateBanner: "Advocacy Update Banner",
  memberSpotlight: "Member Spotlight",
  articleList: "Article List",
  splitArticleWithBorder: "Split Article with Border",
  textAndEmailContent: "Text and Email Content",
  fourGridIconAndTitle: "Four-Grid Icon and Title",
  sixGridIconAndTitle: "Six-Grid Icon and Title",
  upcomingEventsList: "Upcoming Events List",
  eventAnnouncement: "Event Announcement",
  specialGuestSponsor: "Special Guest Sponsor",
  sponsorsList: "Sponsors List (1 to 3 sponsors per block)",
  advertisingPdf: "Advertising PDF",
  keyLinks: "Key Links",
  inquiries: "Inquiries",
  signature: "Signature",
};

// ---------------------------------------------------------------------------
// Repeating-item shapes
// ---------------------------------------------------------------------------

export type EmailBuilderIconGridItem = {
  id: string;
  icon: EmailBuilderIconKey; // icon tile + "Change Icon"
  title: string; // "TITLE"
  linkTo: string; // "LINK TO (OPTIONAL)" — placeholder "Add a link"
  // Renders the icon on a light highlight tile (seen on "Promote Your
  // Business" in the Member Welcome template / Six-Grid screenshot).
  highlighted?: boolean;
};

export type MemberSpotlightItem = {
  id: string;
  imageUrl: string; // image thumbnail + "Change Image"
  title: string; // "TITLE"
  description: string; // "DESCRIPTION"
  buttonName: string; // "BUTTON NAME" — renders as "<name> ›" link
  addLink: string; // "ADD LINK" — placeholder "Add link to redirect the button"
};

export type ArticleListItem = {
  id: string;
  title: string; // "TITLE"
  description: string; // "DESCRIPTION"
};

export type SplitArticleItem = {
  id: string;
  icon: EmailBuilderIconKey; // icon tile + "Change Icon"
  title: string; // "TITLE"
  desc: string; // "DESC"
  buttonName: string; // "BUTTON NAME" — renders as blue caps "<NAME> ›" link
  addLink: string; // "ADD LINK"
};

export type KeyLinkItem = {
  id: string;
  buttonText: string; // "BUTTON TEXT"
  addLink: string; // "ADD LINK"
};

export type EmailBuilderSponsorItem = {
  id: string;
  logoUrl: string; // logo thumbnail + "Change Sponsor Logo"
  linkTo: string; // "LINK TO (OPTIONAL)" — placeholder "Add a link"
};

// Event card used by Upcoming Events List ("EVENT n DETAILS") and Event
// Announcement ("EVENT DETAILS"). Events are picked from the real Events
// module (EMAIL-009): `eventId` stores the picked CmpEvent id and `linkUrl`
// stores the website event registration page URL the "Learn More ›" /
// "Register Now" actions must open (binding annotation).
export type EmailBuilderEventItem = {
  id: string;
  eventId: string;
  eventTitle: string; // "EVENT TITLE"
  date: string; // "DATE"
  time: string; // "TIME"
  location: string; // "LOCATION" (extra lines render as the gray address line)
  linkUrl: string;
};

// ---------------------------------------------------------------------------
// Per-block settings (field order/labels per blocks-a.md / blocks-b.md)
// ---------------------------------------------------------------------------

export type TextAndButtonHeroBannerSettings = {
  backgroundImageUrl: string; // "BACKGROUND IMAGE" + "Change Image"
  title: string; // "TITLE"
  helperText: string; // "HELPER TEXT"
  textColor: string; // "Select Text Color"
  highlightColor: string; // "Select Highlight Color" (button fill)
  backgroundColor: string; // "Select Background Color"
  buttonName: string; // "BUTTON NAME"
  buttonTextColor: string; // "Text Color" (button)
  addLink: string; // "ADD LINK"
};

export type TextHeroBannerSettings = {
  textColor: string; // "Select Text Color"
  backgroundColor: string; // "Select Background Color"
  title: string; // "TITLE"
  textContent: string; // "TEXT CONTENT"
};

export type EmailBannerSettings = {
  textColor: string;
  highlightColor: string; // chip fill
  backgroundColor: string;
  ceoImageUrl: string; // "CEO IMAGE" + "Change Image"
  title: string; // "TITLE" (multi-line; last line renders in highlight italic)
  buttonName: string; // "BUTTON NAME" (renders as the uppercase chip)
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK"
};

export type UpdateBannerSettings = {
  title: string; // "TITLE"
  description: string; // "DESCRIPTION"
  textColor: string;
  highlightColor: string; // button fill
  backgroundColor: string;
  buttonName: string; // "BUTTON NAME" (drives the rendered button label)
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK"
};

export type AlertBannerSettings = {
  alertIcon: EmailBuilderIconKey; // "ALERT ICON" + "Change Icon"
  alertTitle: string; // "ALERT TITLE"
  textColor: string; // "Select Text Color" (live banner text color)
  backgroundColor: string; // "Select Background Color" (no highlight row)
};

export type AdvocacyUpdateBannerSettings = {
  icon: EmailBuilderIconKey; // icon tile + "Change Icon"
  title: string; // "TITLE" (the eyebrow, e.g. ADVOCACY UPDATE)
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  label: string; // "LABEL" (grouped sub-card)
  description: string; // "DESCRIPTION" (grouped sub-card, rich w/ link)
  buttonName: string; // "BUTTON NAME"
  buttonColor: string; // "Button Color"
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK"
};

export type MemberSpotlightSettings = {
  title: string; // "TITLE"
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: MemberSpotlightItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type ArticleListSettings = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: ArticleListItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type SplitArticleWithBorderSettings = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: SplitArticleItem[]; // "+ Add Grid Item"
};

export type TextAndEmailContentSettings = {
  textContent: string; // "TEXT CONTENT" (toolbar includes alignment controls)
  textAlign: "left" | "center" | "justify";
  textColor: string;
  backgroundColor: string;
};

export type FourGridIconAndTitleSettings = {
  title: string; // "TITLE"
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: EmailBuilderIconGridItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type SixGridIconAndTitleSettings = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  title: string; // "TITLE"
  items: EmailBuilderIconGridItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type UpcomingEventsListSettings = {
  title: string; // "TITLE"
  textColor: string;
  imageUrl: string; // image thumbnail + "Change Image"
  highlightColor: string; // date tile / links
  backgroundColor: string;
  events: EmailBuilderEventItem[]; // "EVENT n DETAILS" / "+ Add Another Event Card"
};

export type EventAnnouncementSettings = {
  title: string; // "TITLE" (optional heading; empty by default per template)
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  events: EmailBuilderEventItem[]; // "EVENT DETAILS" / "+ Add Another Event"
};

export type SpecialGuestSponsorSettings = {
  textColor: string;
  backgroundColor: string;
  title: string; // "TITLE"
  sponsorLogoUrl: string; // "SPONSOR LOGO" + "Change Image"
  textContent: string; // "TEXT CONTENT" (italic default)
};

export type SponsorsListSettings = {
  title: string; // "TITLE" (before the color rows on this block)
  textColor: string;
  backgroundColor: string;
  sponsors: EmailBuilderSponsorItem[]; // "SPONSOR LOGO" group / "+ Add Sponsor" (1 to 3)
};

export type AdvertisingPdfSettings = {
  textColor: string;
  highlightColor: string; // button fill
  backgroundColor: string;
  title: string; // "TITLE"
  imageUrl: string; // "IMAGE" + "Change Image"
  buttonName: string; // "BUTTON NAME"
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK" — mutually exclusive with ATTACH PDF ("OR")
  attachPdfUrl: string; // "ATTACH PDF" upload tile
  attachPdfName: string;
};

export type KeyLinksSettings = {
  title: string; // "TITLE"
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: KeyLinkItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type InquiriesSettings = {
  textColor: string;
  backgroundColor: string; // no highlight row on this block
  title: string; // "TITLE"
  textContent: string; // "TEXT CONTENT"
  email: string; // "EMAIL"
  phoneNumber: string; // "PHONE NUMBER"
};

export type SignatureSettings = {
  textColor: string;
  backgroundColor: string;
  addSignature: string; // "ADD SIGNATURE" (script-styled signature line)
  name: string; // "NAME"
  designation: string; // "DESIGNATION"
  chamberName: string; // "CHAMBER NAME"
};

export type EmailBuilderBlockSettingsMap = {
  textAndButtonHeroBanner: TextAndButtonHeroBannerSettings;
  textHeroBanner: TextHeroBannerSettings;
  emailBanner: EmailBannerSettings;
  updateBanner: UpdateBannerSettings;
  alertBanner: AlertBannerSettings;
  advocacyUpdateBanner: AdvocacyUpdateBannerSettings;
  memberSpotlight: MemberSpotlightSettings;
  articleList: ArticleListSettings;
  splitArticleWithBorder: SplitArticleWithBorderSettings;
  textAndEmailContent: TextAndEmailContentSettings;
  fourGridIconAndTitle: FourGridIconAndTitleSettings;
  sixGridIconAndTitle: SixGridIconAndTitleSettings;
  upcomingEventsList: UpcomingEventsListSettings;
  eventAnnouncement: EventAnnouncementSettings;
  specialGuestSponsor: SpecialGuestSponsorSettings;
  sponsorsList: SponsorsListSettings;
  advertisingPdf: AdvertisingPdfSettings;
  keyLinks: KeyLinksSettings;
  inquiries: InquiriesSettings;
  signature: SignatureSettings;
};

export type EmailBuilderBlockOf<T extends EmailBuilderBlockType> = {
  id: string;
  type: T;
  settings: EmailBuilderBlockSettingsMap[T];
};

export type EmailBuilderBlock = {
  [K in EmailBuilderBlockType]: EmailBuilderBlockOf<K>;
}[EmailBuilderBlockType];

// ---------------------------------------------------------------------------
// Builder state (version 2)
// ---------------------------------------------------------------------------

export type EmailBuilderState = {
  version: 2;
  logoUrl: string; // Step 2 "Chamber Logo"
  greetingLine: "firstName" | "fullName" | "companyName" | "none";
  footerText: string; // Step 2 "Footer Text" (one rendered footer line per text line)
  fontFamily: string; // Template Settings tab "CHOOSE FONT" (default Roboto)
  theme: {
    textColor: string;
    highlightColor: string;
    backgroundColor: string; // Template Settings tab "BACKGROUND COLOR"
    footerBackgroundColor: string;
  };
  selectedBlockId: string;
  blocks: EmailBuilderBlock[];
};

export type EmailBuilderStateUpdate =
  | EmailBuilderState
  | ((currentState: EmailBuilderState) => EmailBuilderState);

export type UpdateEmailBuilderState = (
  update: EmailBuilderStateUpdate
) => void;

export type RenderedEmailBuilderContent = {
  /** Full email preview HTML (body + footer). */
  html: string;
  /** Email HTML without the footer — stored as `contentHtml`. */
  bodyHtml: string;
  /** Footer HTML — stored as `footerHtml` so the backend wrapper appends it. */
  footerHtml: string;
  text: string;
};

const defaultTheme = {
  textColor: emailBuilderColors.darkNavy,
  highlightColor: emailBuilderColors.blue,
  backgroundColor: emailBuilderColors.white,
  footerBackgroundColor: emailBuilderColors.black,
};

// Quick "Color Palatte" strip offered next to the free color picker (blue
// theme: blue / light blue / black / white per the Four-Grid composite).
export const getEmailThemeQuickPalette = (theme: {
  highlightColor: string;
}): string[] => [
  theme.highlightColor,
  emailBuilderColors.lightBlue,
  emailBuilderColors.black,
  emailBuilderColors.white,
];

const makeId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// Item factories (used by "+ Add Grid Item" / "+ Add Sponsor" / event cards)
// ---------------------------------------------------------------------------

export const createIconGridItem = (
  overrides: Partial<Omit<EmailBuilderIconGridItem, "id">> = {}
): EmailBuilderIconGridItem => ({
  id: makeId("item"),
  icon: "calendar",
  title: "",
  linkTo: "",
  ...overrides,
});

export const createMemberSpotlightItem = (
  overrides: Partial<Omit<MemberSpotlightItem, "id">> = {}
): MemberSpotlightItem => ({
  id: makeId("item"),
  imageUrl: "",
  title: "",
  description: "",
  buttonName: "Read Their Story",
  addLink: "",
  ...overrides,
});

export const createArticleListItem = (
  overrides: Partial<Omit<ArticleListItem, "id">> = {}
): ArticleListItem => ({
  id: makeId("item"),
  title: "",
  description: "",
  ...overrides,
});

export const createSplitArticleItem = (
  overrides: Partial<Omit<SplitArticleItem, "id">> = {}
): SplitArticleItem => ({
  id: makeId("item"),
  icon: "pencil",
  title: "",
  desc: "",
  buttonName: "",
  addLink: "",
  ...overrides,
});

export const createKeyLinkItem = (
  overrides: Partial<Omit<KeyLinkItem, "id">> = {}
): KeyLinkItem => ({
  id: makeId("item"),
  buttonText: "",
  addLink: "",
  ...overrides,
});

export const createSponsorItem = (
  overrides: Partial<Omit<EmailBuilderSponsorItem, "id">> = {}
): EmailBuilderSponsorItem => ({
  id: makeId("sponsor"),
  logoUrl: "",
  linkTo: "",
  ...overrides,
});

export const createEventItem = (
  overrides: Partial<Omit<EmailBuilderEventItem, "id">> = {}
): EmailBuilderEventItem => ({
  id: makeId("event"),
  eventId: "",
  eventTitle: "",
  date: "",
  time: "",
  location: "",
  linkUrl: "",
  ...overrides,
});

// ---------------------------------------------------------------------------
// Block factories (SAMPLE defaults come from the block screenshots)
// ---------------------------------------------------------------------------

export const createTextAndButtonHeroBannerBlock = (
  overrides: Partial<TextAndButtonHeroBannerSettings> = {}
): EmailBuilderBlockOf<"textAndButtonHeroBanner"> => ({
  id: makeId("textAndButtonHeroBanner"),
  type: "textAndButtonHeroBanner",
  settings: {
    backgroundImageUrl: "",
    title: "The Chamber Voice",
    helperText: "Your Weekly Business Update",
    textColor: emailBuilderColors.white,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.black,
    buttonName: "Read the Full Story",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    ...overrides,
  },
});

export const createTextHeroBannerBlock = (
  overrides: Partial<TextHeroBannerSettings> = {}
): EmailBuilderBlockOf<"textHeroBanner"> => ({
  id: makeId("textHeroBanner"),
  type: "textHeroBanner",
  settings: {
    textColor: emailBuilderColors.white,
    backgroundColor: emailBuilderColors.blue,
    title: "Welcome to the Chamber!",
    textContent: "We're excited to have you as part of our business community.",
    ...overrides,
  },
});

export const createEmailBannerBlock = (
  overrides: Partial<EmailBannerSettings> = {}
): EmailBuilderBlockOf<"emailBanner"> => ({
  id: makeId("emailBanner"),
  type: "emailBanner",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    ceoImageUrl: "",
    title: "From the\nDesk\nof Our CEO",
    buttonName: "Chamber Updates",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    ...overrides,
  },
});

export const createUpdateBannerBlock = (
  overrides: Partial<UpdateBannerSettings> = {}
): EmailBuilderBlockOf<"updateBanner"> => ({
  id: makeId("updateBanner"),
  type: "updateBanner",
  settings: {
    title: "New Provincial Grant Now Available!",
    description:
      "Funding to help local businesses grow and innovate. Take advantage of this exclusive opportunity before the deadline.",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    buttonName: "Apply Now",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    ...overrides,
  },
});

export const createAlertBannerBlock = (
  overrides: Partial<AlertBannerSettings> = {}
): EmailBuilderBlockOf<"alertBanner"> => ({
  id: makeId("alertBanner"),
  type: "alertBanner",
  settings: {
    alertIcon: "warning",
    alertTitle: "IMPORTANT UPDATE",
    textColor: emailBuilderColors.white,
    backgroundColor: emailBuilderColors.blue,
    ...overrides,
  },
});

export const createAdvocacyUpdateBannerBlock = (
  overrides: Partial<AdvocacyUpdateBannerSettings> = {}
): EmailBuilderBlockOf<"advocacyUpdateBanner"> => ({
  id: makeId("advocacyUpdateBanner"),
  type: "advocacyUpdateBanner",
  settings: {
    icon: "megaphone",
    title: "ADVOCACY UPDATE",
    textColor: emailBuilderColors.white,
    highlightColor: emailBuilderColors.veryLightGray,
    backgroundColor: emailBuilderColors.black,
    label: "Protecting Local Interests",
    description:
      "See how your chamber is advocating for business at the local, provincial, and federal levels.",
    buttonName: "View Update",
    buttonColor: emailBuilderColors.white,
    buttonTextColor: emailBuilderColors.darkNavy,
    addLink: "",
    ...overrides,
  },
});

export const createMemberSpotlightBlock = (
  overrides: Partial<MemberSpotlightSettings> = {}
): EmailBuilderBlockOf<"memberSpotlight"> => ({
  id: makeId("memberSpotlight"),
  type: "memberSpotlight",
  settings: {
    title: "Member Spotlight",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.lightBlue,
    items: [
      createMemberSpotlightItem({
        title: "Coastal Coffee Co.",
        description:
          "A locally owned café proudly serving our community with premium coffee and friendly service. Find them at the harbour front this summer!",
        buttonName: "Read Their Story",
      }),
    ],
    ...overrides,
  },
});

export const createArticleListBlock = (
  overrides: Partial<ArticleListSettings> = {}
): EmailBuilderBlockOf<"articleList"> => ({
  id: makeId("articleList"),
  type: "articleList",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createArticleListItem({
        title: "The Issue",
        description:
          "The proposed zoning changes could impact business operations, jobs, and long-term private investment across our central corridors.",
      }),
      createArticleListItem({
        title: "Why It Matters",
        description:
          "These changes affect jobs, investment, and the long-term prosperity of our members and their families.",
      }),
    ],
    ...overrides,
  },
});

export const createSplitArticleWithBorderBlock = (
  overrides: Partial<SplitArticleWithBorderSettings> = {}
): EmailBuilderBlockOf<"splitArticleWithBorder"> => ({
  id: makeId("splitArticleWithBorder"),
  type: "splitArticleWithBorder",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createSplitArticleItem({
        icon: "personAdd",
        title: "New Members",
        desc: "Join us in welcoming our newest business members to the chamber.",
        buttonName: "MEET THEM",
      }),
      createSplitArticleItem({
        icon: "calendar",
        title: "Chamber News",
        desc: "Stay up to date on the latest news and important chamber updates.",
        buttonName: "LATEST NEWS",
      }),
      createSplitArticleItem({
        icon: "pencil",
        title: "From The Desk",
        desc: "A message from our CEO on what's ahead for our community.",
        buttonName: "READ MESSAGE",
      }),
    ],
    ...overrides,
  },
});

export const createTextAndEmailContentBlock = (
  overrides: Partial<TextAndEmailContentSettings> = {}
): EmailBuilderBlockOf<"textAndEmailContent"> => ({
  id: makeId("textAndEmailContent"),
  type: "textAndEmailContent",
  settings: {
    textContent:
      "Join local business leaders for an afternoon of networking, lunch, and valuable insights. Our upcoming luncheon is the perfect opportunity to build lasting professional connections within the Sand Bay community.",
    textAlign: "center",
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    ...overrides,
  },
});

export const createFourGridIconAndTitleBlock = (
  overrides: Partial<FourGridIconAndTitleSettings> = {}
): EmailBuilderBlockOf<"fourGridIconAndTitle"> => ({
  id: makeId("fourGridIconAndTitle"),
  type: "fourGridIconAndTitle",
  settings: {
    title: "What is in the Newsletter?",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createIconGridItem({ icon: "calendar", title: "UPCOMING EVENTS" }),
      createIconGridItem({ icon: "megaphone", title: "CHAMBER NEWS" }),
      createIconGridItem({ icon: "memberSearch", title: "MEMBER SPOTLIGHT" }),
      createIconGridItem({ icon: "personAdd", title: "NEW MEMBERS" }),
    ],
    ...overrides,
  },
});

export const createSixGridIconAndTitleBlock = (
  overrides: Partial<SixGridIconAndTitleSettings> = {}
): EmailBuilderBlockOf<"sixGridIconAndTitle"> => ({
  id: makeId("sixGridIconAndTitle"),
  type: "sixGridIconAndTitle",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    title: "YOUR MEMBERSHIP BENEFITS",
    items: [
      createIconGridItem({ icon: "people", title: "Networking Opportunities" }),
      createIconGridItem({ icon: "briefcase", title: "Business Resources" }),
      createIconGridItem({ icon: "bank", title: "Advocacy & Representation" }),
      createIconGridItem({ icon: "phone", title: "Chamber Perks App™" }),
      createIconGridItem({ icon: "tag", title: "Affinity Programs" }),
      createIconGridItem({
        icon: "megaphone",
        title: "Promote Your Business",
        highlighted: true,
      }),
    ],
    ...overrides,
  },
});

export const createUpcomingEventsListBlock = (
  overrides: Partial<UpcomingEventsListSettings> = {}
): EmailBuilderBlockOf<"upcomingEventsList"> => ({
  id: makeId("upcomingEventsList"),
  type: "upcomingEventsList",
  settings: {
    title: "Upcoming Events",
    textColor: emailBuilderColors.darkNavy,
    imageUrl: "",
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    events: [
      createEventItem({
        eventTitle: "Business Networking Breakfast",
        date: "Jun 12, 2025",
        time: "8:00 AM",
        location: "Cedar Ridge Golf Club",
      }),
    ],
    ...overrides,
  },
});

export const createEventAnnouncementBlock = (
  overrides: Partial<EventAnnouncementSettings> = {}
): EmailBuilderBlockOf<"eventAnnouncement"> => ({
  id: makeId("eventAnnouncement"),
  type: "eventAnnouncement",
  settings: {
    title: "",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    events: [
      createEventItem({
        eventTitle: "Business Networking Luncheon",
        date: "Tuesday, June 24, 2025",
        time: "11:30 AM – 1:30 PM",
        location: "Harbour View Event Centre\n123 Harbour Dr., Sand Bay",
      }),
    ],
    ...overrides,
  },
});

export const createSpecialGuestSponsorBlock = (
  overrides: Partial<SpecialGuestSponsorSettings> = {}
): EmailBuilderBlockOf<"specialGuestSponsor"> => ({
  id: makeId("specialGuestSponsor"),
  type: "specialGuestSponsor",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    title: "SPECIAL GUEST SPONSOR",
    sponsorLogoUrl: "",
    textContent: "Thank you to our event sponsor for making this possible!",
    ...overrides,
  },
});

export const createSponsorsListBlock = (
  overrides: Partial<SponsorsListSettings> = {}
): EmailBuilderBlockOf<"sponsorsList"> => ({
  id: makeId("sponsorsList"),
  type: "sponsorsList",
  settings: {
    title: "OUR SPONSORS",
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    sponsors: [createSponsorItem()],
    ...overrides,
  },
});

export const createAdvertisingPdfBlock = (
  overrides: Partial<AdvertisingPdfSettings> = {}
): EmailBuilderBlockOf<"advertisingPdf"> => ({
  id: makeId("advertisingPdf"),
  type: "advertisingPdf",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    title: "Best Mortgage Rates",
    imageUrl: "",
    buttonName: "View Full PDF",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    attachPdfUrl: "",
    attachPdfName: "",
    ...overrides,
  },
});

export const createKeyLinksBlock = (
  overrides: Partial<KeyLinksSettings> = {}
): EmailBuilderBlockOf<"keyLinks"> => ({
  id: makeId("keyLinks"),
  type: "keyLinks",
  settings: {
    title: "Key Links",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createKeyLinkItem({ buttonText: "Read the Full Brief" }),
      createKeyLinkItem({ buttonText: "View Proposed Changes" }),
      createKeyLinkItem({ buttonText: "Contact Your Representative" }),
    ],
    ...overrides,
  },
});

export const createInquiriesBlock = (
  overrides: Partial<InquiriesSettings> = {}
): EmailBuilderBlockOf<"inquiries"> => ({
  id: makeId("inquiries"),
  type: "inquiries",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    title: "For Media Inquiries",
    textContent: "Karen Hattie, Executive Director",
    email: "info@sandbaychamber.ca",
    phoneNumber: "(705) 555-1234",
    ...overrides,
  },
});

export const createSignatureBlock = (
  overrides: Partial<SignatureSettings> = {}
): EmailBuilderBlockOf<"signature"> => ({
  id: makeId("signature"),
  type: "signature",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    addSignature: "Karen Hattie",
    name: "Karen Hattie",
    designation: "CEO",
    chamberName: "SAND BAY CHAMBER OF COMMERCE",
    ...overrides,
  },
});

const blockFactories: {
  [K in EmailBuilderBlockType]: () => EmailBuilderBlockOf<K>;
} = {
  textAndButtonHeroBanner: createTextAndButtonHeroBannerBlock,
  textHeroBanner: createTextHeroBannerBlock,
  emailBanner: createEmailBannerBlock,
  updateBanner: createUpdateBannerBlock,
  alertBanner: createAlertBannerBlock,
  advocacyUpdateBanner: createAdvocacyUpdateBannerBlock,
  memberSpotlight: createMemberSpotlightBlock,
  articleList: createArticleListBlock,
  splitArticleWithBorder: createSplitArticleWithBorderBlock,
  textAndEmailContent: createTextAndEmailContentBlock,
  fourGridIconAndTitle: createFourGridIconAndTitleBlock,
  sixGridIconAndTitle: createSixGridIconAndTitleBlock,
  upcomingEventsList: createUpcomingEventsListBlock,
  eventAnnouncement: createEventAnnouncementBlock,
  specialGuestSponsor: createSpecialGuestSponsorBlock,
  sponsorsList: createSponsorsListBlock,
  advertisingPdf: createAdvertisingPdfBlock,
  keyLinks: createKeyLinksBlock,
  inquiries: createInquiriesBlock,
  signature: createSignatureBlock,
};

export const createEmailBuilderBlock = (
  type: EmailBuilderBlockType
): EmailBuilderBlock => blockFactories[type]();

// ---------------------------------------------------------------------------
// Palette metadata (packaged preview images — EMAIL-005)
// ---------------------------------------------------------------------------

export type EmailBuilderPaletteItem = {
  key: EmailBuilderBlockType;
  label: string;
  previewImage: string;
};

// Palette order matches the builder screenshots (general design of content
// builder, Screenshots 3-6).
export const emailBuilderPaletteItems: EmailBuilderPaletteItem[] = [
  { key: "textAndButtonHeroBanner", label: emailBuilderBlockLabels.textAndButtonHeroBanner, previewImage: textAndButtonHeroBannerPreview },
  { key: "fourGridIconAndTitle", label: emailBuilderBlockLabels.fourGridIconAndTitle, previewImage: fourGridIconAndTitlePreview },
  { key: "upcomingEventsList", label: emailBuilderBlockLabels.upcomingEventsList, previewImage: upcomingEventsListPreview },
  { key: "memberSpotlight", label: emailBuilderBlockLabels.memberSpotlight, previewImage: memberSpotlightPreview },
  { key: "advocacyUpdateBanner", label: emailBuilderBlockLabels.advocacyUpdateBanner, previewImage: advocacyUpdateBannerPreview },
  { key: "splitArticleWithBorder", label: emailBuilderBlockLabels.splitArticleWithBorder, previewImage: splitArticleWithBorderPreview },
  { key: "alertBanner", label: emailBuilderBlockLabels.alertBanner, previewImage: alertBannerPreview },
  { key: "updateBanner", label: emailBuilderBlockLabels.updateBanner, previewImage: updateBannerPreview },
  { key: "articleList", label: emailBuilderBlockLabels.articleList, previewImage: articleListPreview },
  { key: "keyLinks", label: emailBuilderBlockLabels.keyLinks, previewImage: keyLinksPreview },
  { key: "eventAnnouncement", label: emailBuilderBlockLabels.eventAnnouncement, previewImage: eventAnnouncementPreview },
  { key: "textAndEmailContent", label: emailBuilderBlockLabels.textAndEmailContent, previewImage: textAndEmailContentPreview },
  { key: "specialGuestSponsor", label: emailBuilderBlockLabels.specialGuestSponsor, previewImage: specialGuestSponsorPreview },
  { key: "inquiries", label: emailBuilderBlockLabels.inquiries, previewImage: inquiriesPreview },
  { key: "textHeroBanner", label: emailBuilderBlockLabels.textHeroBanner, previewImage: textHeroBannerPreview },
  { key: "sixGridIconAndTitle", label: emailBuilderBlockLabels.sixGridIconAndTitle, previewImage: sixGridIconAndTitlePreview },
  { key: "emailBanner", label: emailBuilderBlockLabels.emailBanner, previewImage: emailBannerPreview },
  { key: "signature", label: emailBuilderBlockLabels.signature, previewImage: signaturePreview },
  { key: "sponsorsList", label: emailBuilderBlockLabels.sponsorsList, previewImage: sponsorsListPreview },
  { key: "advertisingPdf", label: emailBuilderBlockLabels.advertisingPdf, previewImage: advertisingPdfPreview },
];

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

export const buildDefaultEmailBuilderState = (
  chamberName?: string
): EmailBuilderState => {
  const name = chamberName?.trim() || "{INSERT CHAMBER NAME}";

  return {
    version: 2,
    logoUrl: "",
    greetingLine: "none",
    footerText: `© ${new Date().getFullYear()} ${name}\nYou are receiving this email because you are a valued member of the ${name} business community.`,
    fontFamily: "Roboto",
    theme: { ...defaultTheme },
    selectedBlockId: "",
    // Custom Email = empty canvas; templates seed blocks via emailTemplatePresets.
    blocks: [],
  };
};

export const cloneEmailBuilderState = (
  builderState: EmailBuilderState
): EmailBuilderState =>
  JSON.parse(JSON.stringify(builderState)) as EmailBuilderState;

// ---------------------------------------------------------------------------
// HTML rendering (conservative table/inline-style email HTML)
// ---------------------------------------------------------------------------

export type EmailBuilderRenderTheme = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  footerBackgroundColor: string;
  fontFamily: string;
};

const getRenderTheme = (state: EmailBuilderState): EmailBuilderRenderTheme => ({
  ...state.theme,
  fontFamily: state.fontFamily || "Roboto",
});

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Rich-text fields tolerate the simple B/U/I markup produced by the settings
// panels; everything else is escaped.
const richTextToHtml = (value: string): string =>
  escapeHtml(value)
    .replace(/&lt;(\/?)(b|strong|i|em|u|br)\s*\/?&gt;/gi, "<$1$2>")
    .replace(/\n/g, "<br />");

const stripSimpleMarkup = (value: string): string =>
  value.replace(/<\/?(b|strong|i|em|u|br)\s*\/?>/gi, "");

const fontStack = (fontFamily: string) =>
  `'${fontFamily}', Arial, Helvetica, sans-serif`;

const sectionShell = ({
  inner,
  background,
  padding = "32px 36px",
  align = "center",
}: {
  inner: string;
  background: string;
  padding?: string;
  align?: string;
}): string =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="background:${background};padding:${padding};text-align:${align};">${inner}</td></tr></table>`;

const ctaButton = ({
  label,
  url,
  background,
  color,
  align = "center",
  margin = "24px auto 0",
}: {
  label: string;
  url: string;
  background: string;
  color: string;
  align?: "left" | "center";
  margin?: string;
}): string =>
  label.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:separate;margin:${
        align === "center" ? margin : "24px 0 0"
      };"><tr><td style="background:${background};border-radius:8px;text-align:center;"><a href="${escapeHtml(
        url || "#"
      )}" style="display:inline-block;padding:13px 28px;color:${color};font-size:15px;font-weight:700;text-decoration:none;">${richTextToHtml(
        label
      )}</a></td></tr></table>`
    : "";

const iconTile = ({
  icon,
  color,
  background = "transparent",
  size = 54,
  rounded = 12,
  border = true,
}: {
  icon: EmailBuilderIconKey;
  color: string;
  background?: string;
  size?: number;
  rounded?: number;
  border?: boolean;
}): string =>
  `<div style="display:inline-block;width:${size}px;height:${size}px;line-height:${size}px;text-align:center;font-size:${Math.round(
    size * 0.45
  )}px;color:${color};background:${background};border:${
    border ? `2px solid ${color}` : "none"
  };border-radius:${rounded}px;">${emailBuilderIconGlyphs[icon]}</div>`;

const imageOrPlaceholder = ({
  imageUrl,
  alt,
  placeholderLabel,
  maxWidth = "100%",
  height,
  layoutWidth,
}: {
  imageUrl: string;
  alt: string;
  placeholderLabel: string;
  maxWidth?: string;
  height?: number;
  layoutWidth?: number;
}): string => {
  const maxWidthPixels = maxWidth.match(/^(\d+)px$/)?.[1];
  const emailLayoutWidth =
    layoutWidth || (maxWidthPixels ? Number(maxWidthPixels) : 600);

  return imageUrl
    ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(
        alt
      )}" width="${emailLayoutWidth}"${
        height ? ` height="${height}"` : ""
      } style="display:block;width:100%;max-width:${maxWidth};${
        height ? `height:${height}px;object-fit:cover;` : "height:auto;"
      }margin:0 auto;border-radius:8px;" />`
    : `<div style="display:inline-block;width:100%;max-width:${maxWidth};${
        height ? `height:${height}px;line-height:${height}px;` : "padding:26px 0;"
      }border:1px dashed #cbd5e1;border-radius:8px;color:#94a3b8;font-size:13px;text-align:center;">${escapeHtml(
        placeholderLabel
      )}</div>`;
};

const WEEKDAY_TOKENS = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const getEventDateParts = (
  date: string
): { month: string; day: string; weekdayPrefix: string } => {
  const trimmed = date.trim();
  const startsWithWeekday = WEEKDAY_TOKENS.includes(
    trimmed.split(/[\s,]+/)[0]?.toLowerCase() || ""
  );
  const parsed = new Date(trimmed);

  if (!Number.isNaN(parsed.getTime())) {
    return {
      month: parsed
        .toLocaleString("en-US", { month: "short" })
        .toUpperCase(),
      day: String(parsed.getDate()),
      weekdayPrefix: startsWithWeekday
        ? ""
        : `${parsed.toLocaleString("en-US", { weekday: "short" })}, `,
    };
  }

  const tokens = trimmed.split(/[\s,]+/).filter(Boolean);

  return {
    month: (tokens[0] || "").slice(0, 3).toUpperCase(),
    day: tokens[1] || "",
    weekdayPrefix: "",
  };
};

export const renderTextAndButtonHeroBanner = (
  block: EmailBuilderBlockOf<"textAndButtonHeroBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  // Outlook desktop does not reliably render CSS background images. The MSO
  // conditional keeps the uploaded visual in those clients as a normal image,
  // while modern clients retain the intended text-over-background treatment.
  const outlookBackgroundImageFallback = s.backgroundImageUrl
    ? `<!--[if mso]><div style="margin:0 0 24px;"><img src="${escapeHtml(
        s.backgroundImageUrl
      )}" alt="" width="600" style="display:block;width:100%;max-width:600px;height:auto;" /></div><![endif]-->`
    : "";
  const background = s.backgroundImageUrl
    ? `${s.backgroundColor} url('${escapeHtml(
        s.backgroundImageUrl
      )}') center / cover no-repeat`
    : s.backgroundColor;

  return sectionShell({
    inner: `${outlookBackgroundImageFallback}<h1 style="margin:0;font-family:${fontStack(
      theme.fontFamily
    )};color:${s.textColor};font-size:38px;line-height:1.15;font-weight:800;">${richTextToHtml(
      s.title
    )}</h1><p style="margin:12px 0 0;font-family:${fontStack(
      theme.fontFamily
    )};color:${s.textColor};font-size:20px;line-height:1.4;font-weight:600;">${richTextToHtml(
      s.helperText
    )}</p>${ctaButton({
      label: s.buttonName,
      url: s.addLink,
      background: s.highlightColor,
      color: s.buttonTextColor,
      align: "left",
    })}`,
    background,
    padding: "56px 40px",
    align: "left",
  });
};

export const renderTextHeroBanner = (
  block: EmailBuilderBlockOf<"textHeroBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;

  return sectionShell({
    inner: `<h2 style="margin:0;font-family:${fontStack(
      theme.fontFamily
    )};color:${s.textColor};font-size:30px;line-height:1.2;font-weight:800;">${richTextToHtml(
      s.title
    )}</h2><p style="margin:10px 0 0;font-family:${fontStack(
      theme.fontFamily
    )};color:${s.textColor};font-size:16px;line-height:1.6;">${richTextToHtml(
      s.textContent
    )}</p>`,
    background: s.backgroundColor,
    padding: "38px 36px",
  });
};

export const renderEmailBanner = (
  block: EmailBuilderBlockOf<"emailBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const titleLines = s.title.split("\n").filter((line) => line.trim());
  const titleHtml = titleLines
    .map((line, index) => {
      const isAccentLine = titleLines.length > 1 && index === titleLines.length - 1;
      return `<span style="display:block;color:${
        isAccentLine ? s.highlightColor : s.textColor
      };${isAccentLine ? "font-style:italic;" : ""}">${richTextToHtml(line)}</span>`;
    })
    .join("");
  const chip = s.buttonName.trim()
    ? `<a href="${escapeHtml(
        s.addLink || "#"
      )}" style="display:inline-block;background:${s.highlightColor};color:${
        s.buttonTextColor
      };font-size:13px;font-weight:700;letter-spacing:2px;text-transform:uppercase;text-decoration:none;padding:10px 18px;border-radius:4px;">${richTextToHtml(
        s.buttonName
      )}</a>`
    : "";

  return sectionShell({
    inner: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="vertical-align:middle;width:50%;padding-right:18px;text-align:left;">${chip}<h2 style="margin:${
      chip ? "18px" : "0"
    } 0 0;font-family:${fontStack(
      theme.fontFamily
    )};font-size:40px;line-height:1.1;font-weight:800;">${titleHtml}</h2></td><td style="vertical-align:middle;width:50%;text-align:right;">${imageOrPlaceholder(
      {
        imageUrl: s.ceoImageUrl,
        alt: "",
        placeholderLabel: "CEO Image",
        maxWidth: "100%",
        height: 220,
        layoutWidth: 260,
      }
    )}</td></tr></table>`,
    background: s.backgroundColor,
    padding: "34px 36px",
    align: "left",
  });
};

export const renderUpdateBanner = (
  block: EmailBuilderBlockOf<"updateBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const heading = s.title.trim()
    ? `<h2 style="margin:0;font-family:${fontStack(theme.fontFamily)};color:${
        s.textColor
      };font-size:30px;line-height:1.2;font-weight:800;">${richTextToHtml(
        s.title
      )}</h2>`
    : "";
  const body = s.description.trim()
    ? `<p style="margin:${
        heading ? "12px" : "0"
      } 0 0;font-family:${fontStack(
        theme.fontFamily
      )};color:#4b5563;font-size:16px;line-height:1.6;">${richTextToHtml(
        s.description
      )}</p>`
    : "";

  return sectionShell({
    inner: `${heading}${body}${ctaButton({
      label: s.buttonName,
      url: s.addLink,
      background: s.highlightColor,
      color: s.buttonTextColor,
    })}`,
    background: s.backgroundColor,
    padding: "38px 36px",
  });
};

export const renderAlertBanner = (
  block: EmailBuilderBlockOf<"alertBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;

  return sectionShell({
    inner: `<span style="font-family:${fontStack(theme.fontFamily)};color:${
      s.textColor
    };font-size:17px;font-weight:800;letter-spacing:3px;"><span style="font-size:18px;margin-right:10px;">${
      emailBuilderIconGlyphs[s.alertIcon]
    }</span>${richTextToHtml(s.alertTitle)}</span>`,
    background: s.backgroundColor,
    padding: "18px 36px",
  });
};

export const renderAdvocacyUpdateBanner = (
  block: EmailBuilderBlockOf<"advocacyUpdateBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const button = s.buttonName.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:separate;margin:0 0 0 auto;"><tr><td style="background:${
        s.buttonColor
      };border-radius:10px;text-align:center;"><a href="${escapeHtml(
        s.addLink || "#"
      )}" style="display:inline-block;padding:14px 22px;color:${
        s.buttonTextColor
      };font-size:15px;font-weight:700;text-decoration:none;">${richTextToHtml(
        s.buttonName
      )}</a></td></tr></table>`
    : "";

  return sectionShell({
    inner: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;background:${
      s.backgroundColor
    };border-radius:12px;"><tr><td style="padding:30px 32px;text-align:left;vertical-align:middle;"><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:13px;font-weight:700;letter-spacing:3px;"><span style="margin-right:8px;">${
      emailBuilderIconGlyphs[s.icon]
    }</span>${richTextToHtml(
      s.title
    )}</p><h2 style="margin:12px 0 0;font-family:${font};color:${
      s.textColor
    };font-size:28px;line-height:1.2;font-weight:800;">${richTextToHtml(
      s.label
    )}</h2><p style="margin:10px 0 0;font-family:${font};color:${
      s.highlightColor
    };font-size:15px;line-height:1.6;">${richTextToHtml(
      s.description
    )}</p></td><td style="padding:30px 32px 30px 0;vertical-align:middle;width:130px;">${button}</td></tr></table>`,
    background: emailBuilderColors.white,
    padding: "26px 36px",
    align: "left",
  });
};

export const renderMemberSpotlight = (
  block: EmailBuilderBlockOf<"memberSpotlight">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const itemsHtml = s.items
    .map(
      (item) =>
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-top:22px;"><tr><td style="width:190px;vertical-align:top;">${imageOrPlaceholder(
          {
            imageUrl: item.imageUrl,
            alt: stripSimpleMarkup(item.title),
            placeholderLabel: "Add an image",
            maxWidth: "180px",
            height: 150,
          }
        )}</td><td style="vertical-align:top;padding-left:22px;text-align:left;"><h3 style="margin:0;font-family:${font};color:${
          s.textColor
        };font-size:20px;font-weight:800;">${richTextToHtml(
          item.title
        )}</h3><p style="margin:10px 0 0;font-family:${font};color:#4b5563;font-size:15px;line-height:1.6;">${richTextToHtml(
          item.description
        )}</p>${
          item.buttonName.trim()
            ? `<p style="margin:14px 0 0;"><a href="${escapeHtml(
                item.addLink || "#"
              )}" style="font-family:${font};color:${
                s.highlightColor
              };font-size:15px;font-weight:700;text-decoration:underline;">${richTextToHtml(
                item.buttonName
              )} &rsaquo;</a></p>`
            : ""
        }</td></tr></table>`
    )
    .join("");

  return sectionShell({
    inner: `<h2 style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:28px;font-weight:800;text-align:center;">${richTextToHtml(
      s.title
    )}</h2>${itemsHtml}`,
    background: s.backgroundColor,
    padding: "34px 36px",
    align: "left",
  });
};

export const renderArticleList = (
  block: EmailBuilderBlockOf<"articleList">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const itemsHtml = s.items
    .map(
      (item, index) =>
        `<h3 style="margin:${
          index === 0 ? "0" : "26px"
        } 0 0;padding-bottom:10px;border-bottom:1px solid #e5e7eb;font-family:${font};color:${
          s.textColor
        };font-size:20px;font-weight:800;">${richTextToHtml(
          item.title
        )}</h3><p style="margin:12px 0 0;font-family:${font};color:#4b5563;font-size:15px;line-height:1.6;">${richTextToHtml(
          item.description
        )}</p>`
    )
    .join("");

  return sectionShell({
    inner: itemsHtml,
    background: s.backgroundColor,
    padding: "30px 36px",
    align: "left",
  });
};

export const renderSplitArticleWithBorder = (
  block: EmailBuilderBlockOf<"splitArticleWithBorder">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const width = s.items.length > 0 ? Math.floor(100 / s.items.length) : 100;
  const columns = s.items
    .map(
      (item, index) =>
        `<td style="width:${width}%;vertical-align:top;padding:6px 16px;${
          index > 0 ? "border-left:1px solid #e5e7eb;" : ""
        }text-align:left;"><div style="color:${
          s.highlightColor
        };font-size:22px;">${
          emailBuilderIconGlyphs[item.icon]
        }</div><h4 style="margin:10px 0 0;font-family:${font};color:${
          s.textColor
        };font-size:17px;font-weight:800;">${richTextToHtml(
          item.title
        )}</h4><p style="margin:8px 0 0;font-family:${font};color:#4b5563;font-size:13px;line-height:1.6;">${richTextToHtml(
          item.desc
        )}</p>${
          item.buttonName.trim()
            ? `<p style="margin:12px 0 0;"><a href="${escapeHtml(
                item.addLink || "#"
              )}" style="font-family:${font};color:${
                s.highlightColor
              };font-size:12px;font-weight:800;letter-spacing:1px;text-transform:uppercase;text-decoration:none;">${richTextToHtml(
                item.buttonName
              )} &rsaquo;</a></p>`
            : ""
        }</td>`
    )
    .join("");

  return sectionShell({
    inner: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr>${columns}</tr></table>`,
    background: s.backgroundColor,
    padding: "30px 24px",
    align: "left",
  });
};

export const renderTextAndEmailContent = (
  block: EmailBuilderBlockOf<"textAndEmailContent">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const paragraphs = s.textContent
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map(
      (paragraph, index) =>
        `<p style="margin:${index === 0 ? "0" : "16px"} 0 0;font-family:${fontStack(
          theme.fontFamily
        )};color:${s.textColor};font-size:16px;line-height:1.7;text-align:${
          s.textAlign
        };">${richTextToHtml(paragraph)}</p>`
    )
    .join("");

  return sectionShell({
    inner: paragraphs,
    background: s.backgroundColor,
    padding: "30px 36px",
    align: s.textAlign === "justify" ? "left" : s.textAlign,
  });
};

const renderIconGrid = ({
  settings,
  theme,
  columns,
}: {
  settings: {
    title: string;
    textColor: string;
    highlightColor: string;
    backgroundColor: string;
    items: EmailBuilderIconGridItem[];
  };
  theme: EmailBuilderRenderTheme;
  columns: number;
}): string => {
  const font = fontStack(theme.fontFamily);
  const rows: EmailBuilderIconGridItem[][] = [];
  settings.items.forEach((item, index) => {
    const rowIndex = Math.floor(index / columns);
    rows[rowIndex] = rows[rowIndex] || [];
    rows[rowIndex].push(item);
  });
  const rowsHtml = rows
    .map((rowItems) => {
      const cells = rowItems
        .map((item) => {
          const label = `<div style="margin-top:10px;font-family:${font};color:${
            settings.textColor
          };font-size:13px;font-weight:700;">${richTextToHtml(item.title)}</div>`;

          return `<td style="width:${Math.floor(
            100 / columns
          )}%;padding:14px 8px;vertical-align:top;text-align:center;">${
            item.linkTo.trim()
              ? `<a href="${escapeHtml(
                  item.linkTo
                )}" style="text-decoration:none;">`
              : ""
          }${iconTile({
            icon: item.icon,
            color: settings.highlightColor,
            background: item.highlighted
              ? emailBuilderColors.lightBlue
              : "transparent",
            border: !item.highlighted,
          })}${label}${item.linkTo.trim() ? "</a>" : ""}</td>`;
        })
        .join("");

      return `<tr>${cells}</tr>`;
    })
    .join("");
  const heading = settings.title.trim()
    ? `<h2 style="margin:0 0 18px;font-family:${font};color:${
        settings.textColor
      };font-size:24px;font-weight:800;text-align:center;">${richTextToHtml(
        settings.title
      )}</h2>`
    : "";

  return sectionShell({
    inner: `${heading}<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">${rowsHtml}</table>`,
    background: settings.backgroundColor,
    padding: "32px 30px",
  });
};

export const renderFourGridIconAndTitle = (
  block: EmailBuilderBlockOf<"fourGridIconAndTitle">,
  theme: EmailBuilderRenderTheme
): string =>
  renderIconGrid({
    settings: block.settings,
    theme,
    columns: Math.min(Math.max(block.settings.items.length, 1), 5),
  });

export const renderSixGridIconAndTitle = (
  block: EmailBuilderBlockOf<"sixGridIconAndTitle">,
  theme: EmailBuilderRenderTheme
): string => renderIconGrid({ settings: block.settings, theme, columns: 3 });

export const renderUpcomingEventsList = (
  block: EmailBuilderBlockOf<"upcomingEventsList">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const imageHtml = s.imageUrl
    ? `<div style="margin-bottom:18px;">${imageOrPlaceholder({
        imageUrl: s.imageUrl,
        alt: "",
        placeholderLabel: "",
        maxWidth: "100%",
        layoutWidth: 600,
      })}</div>`
    : "";
  const eventsHtml = s.events
    .map((event) => {
      const dateParts = getEventDateParts(event.date);

      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;border:1px solid #e5e7eb;border-radius:10px;margin-top:16px;"><tr><td style="width:64px;background:${
        s.highlightColor
      };border-radius:10px 0 0 10px;text-align:center;vertical-align:middle;padding:16px 0;"><div style="font-family:${font};color:#ffffff;font-size:12px;font-weight:700;letter-spacing:1px;">${escapeHtml(
        dateParts.month
      )}</div><div style="font-family:${font};color:#ffffff;font-size:24px;font-weight:800;">${escapeHtml(
        dateParts.day
      )}</div></td><td style="padding:16px 18px;text-align:left;vertical-align:middle;"><h4 style="margin:0;font-family:${font};color:${
        s.textColor
      };font-size:17px;font-weight:800;">${richTextToHtml(
        event.eventTitle
      )}</h4><p style="margin:6px 0 0;font-family:${font};color:#6b7280;font-size:13px;">${escapeHtml(
        `${dateParts.weekdayPrefix}${event.date}`
      )}${event.time.trim() ? ` | ${escapeHtml(event.time)}` : ""}</p><p style="margin:4px 0 0;font-family:${font};color:#6b7280;font-size:13px;">${richTextToHtml(
        event.location.split("\n")[0] || ""
      )}</p></td><td style="padding:16px 18px;text-align:right;vertical-align:middle;white-space:nowrap;"><a href="${escapeHtml(
        event.linkUrl || "#"
      )}" style="font-family:${font};color:${
        s.highlightColor
      };font-size:14px;font-weight:700;text-decoration:none;">Learn More &rsaquo;</a></td></tr></table>`;
    })
    .join("");

  return sectionShell({
    inner: `${imageHtml}<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="width:6px;background:${
      s.highlightColor
    };border-radius:3px;"></td><td style="padding-left:12px;"><h2 style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:24px;font-weight:800;">${richTextToHtml(
      s.title
    )}</h2></td></tr></table>${eventsHtml}`,
    background: s.backgroundColor,
    padding: "30px 36px",
    align: "left",
  });
};

export const renderEventAnnouncement = (
  block: EmailBuilderBlockOf<"eventAnnouncement">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const heading = s.title.trim()
    ? `<h2 style="margin:0 0 20px;font-family:${font};color:${
        s.textColor
      };font-size:26px;font-weight:800;text-align:center;">${richTextToHtml(
        s.title
      )}</h2>`
    : "";
  const detailRow = (icon: EmailBuilderIconKey, content: string): string =>
    `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-top:14px;"><tr><td style="width:46px;vertical-align:middle;">${iconTile(
      {
        icon,
        color: s.highlightColor,
        background: emailBuilderColors.lightBlue,
        size: 42,
        rounded: 10,
        border: false,
      }
    )}</td><td style="padding-left:14px;vertical-align:middle;text-align:left;">${content}</td></tr></table>`;
  const eventsHtml = s.events
    .map((event, index) => {
      const locationLines = event.location
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      const locationHtml = `<p style="margin:0;font-family:${font};color:${
        s.textColor
      };font-size:16px;font-weight:700;">${richTextToHtml(
        locationLines[0] || ""
      )}</p>${locationLines
        .slice(1)
        .map(
          (line) =>
            `<p style="margin:2px 0 0;font-family:${font};color:#6b7280;font-size:13px;">${richTextToHtml(
              line
            )}</p>`
        )
        .join("")}`;

      return `${
        index > 0
          ? '<hr style="border:none;border-top:1px solid #e5e7eb;margin:26px 0 12px;" />'
          : ""
      }${
        event.eventTitle.trim()
          ? `<h3 style="margin:${
              index === 0 && !heading ? "0" : "18px"
            } 0 4px;font-family:${font};color:${
              s.textColor
            };font-size:20px;font-weight:800;text-align:left;">${richTextToHtml(
              event.eventTitle
            )}</h3>`
          : ""
      }${detailRow(
        "calendar",
        `<p style="margin:0;font-family:${font};color:${
          s.textColor
        };font-size:16px;font-weight:700;">${richTextToHtml(event.date)}</p>`
      )}${detailRow(
        "clock",
        `<p style="margin:0;font-family:${font};color:${
          s.textColor
        };font-size:16px;font-weight:700;">${richTextToHtml(event.time)}</p>`
      )}${detailRow("mapPin", locationHtml)}${ctaButton({
        label: "Register Now",
        url: event.linkUrl,
        background: s.highlightColor,
        color: "#ffffff",
      })}`;
    })
    .join("");

  return sectionShell({
    inner: `${heading}${eventsHtml}`,
    background: s.backgroundColor,
    padding: "32px 36px",
    align: "left",
  });
};

export const renderSpecialGuestSponsor = (
  block: EmailBuilderBlockOf<"specialGuestSponsor">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);

  return sectionShell({
    inner: `<div style="border:1px solid #e5e7eb;border-radius:10px;padding:26px 24px;text-align:center;"><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:15px;font-weight:800;letter-spacing:3px;">${richTextToHtml(
      s.title
    )}</p><div style="margin:18px auto 0;max-width:260px;">${imageOrPlaceholder({
      imageUrl: s.sponsorLogoUrl,
      alt: "Sponsor logo",
      placeholderLabel: "Sponsor Logo",
      maxWidth: "260px",
      height: 90,
    })}</div><p style="margin:16px 0 0;font-family:${font};color:${
      s.textColor
    };font-size:14px;font-style:italic;">${richTextToHtml(
      s.textContent
    )}</p></div>`,
    background: s.backgroundColor,
    padding: "26px 36px",
  });
};

export const renderSponsorsList = (
  block: EmailBuilderBlockOf<"sponsorsList">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const sponsors = s.sponsors.slice(0, 3);
  const cells = sponsors
    .map(
      (sponsor) =>
        `<td style="width:${Math.floor(
          100 / Math.max(sponsors.length, 1)
        )}%;padding:8px;text-align:center;vertical-align:middle;">${
          sponsor.linkTo.trim()
            ? `<a href="${escapeHtml(sponsor.linkTo)}" style="text-decoration:none;">`
            : ""
        }<div style="border:1px solid #e5e7eb;border-radius:8px;padding:14px;">${imageOrPlaceholder(
          {
            imageUrl: sponsor.logoUrl,
            alt: "Sponsor logo",
            placeholderLabel: "Sponsor Logo",
            maxWidth: "100%",
            height: 70,
            layoutWidth: 150,
          }
        )}</div>${sponsor.linkTo.trim() ? "</a>" : ""}</td>`
    )
    .join("");

  return sectionShell({
    inner: `<div style="border:1px solid #e5e7eb;border-radius:10px;padding:24px;"><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:15px;font-weight:800;letter-spacing:3px;text-align:center;">${richTextToHtml(
      s.title
    )}</p><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-top:14px;"><tr>${cells}</tr></table></div>`,
    background: s.backgroundColor,
    padding: "26px 36px",
  });
};

export const renderAdvertisingPdf = (
  block: EmailBuilderBlockOf<"advertisingPdf">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  // The button opens the link OR the attached PDF (mutually exclusive per the
  // settings sheet's literal "OR" separator).
  const destination = s.addLink.trim() || s.attachPdfUrl.trim();

  return sectionShell({
    inner: `<h2 style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:30px;font-weight:800;">${richTextToHtml(
      s.title
    )}</h2><div style="margin:20px auto 0;max-width:300px;border:1px solid #e5e7eb;border-radius:10px;padding:18px;">${imageOrPlaceholder(
      {
        imageUrl: s.imageUrl,
        alt: stripSimpleMarkup(s.title),
        placeholderLabel: "Advertiser Image",
        maxWidth: "260px",
        height: 110,
      }
    )}</div>${ctaButton({
      label: s.buttonName,
      url: destination,
      background: s.highlightColor,
      color: s.buttonTextColor,
    })}`,
    background: s.backgroundColor,
    padding: "36px 36px",
  });
};

export const renderKeyLinks = (
  block: EmailBuilderBlockOf<"keyLinks">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const linksHtml = s.items
    .map(
      (item) =>
        `<p style="margin:14px 0 0;"><a href="${escapeHtml(
          item.addLink || "#"
        )}" style="font-family:${font};color:${
          s.highlightColor
        };font-size:15px;font-weight:700;text-decoration:none;"><span style="margin-right:10px;">&rarr;</span>${richTextToHtml(
          item.buttonText
        )}</a></p>`
    )
    .join("");

  return sectionShell({
    inner: `<div style="border:1px solid #e5e7eb;border-radius:10px;padding:24px;text-align:left;"><h3 style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:20px;font-weight:800;">${richTextToHtml(
      s.title
    )}</h3>${linksHtml}</div>`,
    background: s.backgroundColor,
    padding: "26px 36px",
    align: "left",
  });
};

export const renderInquiries = (
  block: EmailBuilderBlockOf<"inquiries">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);

  return sectionShell({
    inner: `<div style="border:1px solid #e5e7eb;border-radius:10px;padding:24px;text-align:left;"><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:14px;font-weight:800;">${richTextToHtml(
      s.title
    )}</p><p style="margin:10px 0 0;font-family:${font};color:${
      s.textColor
    };font-size:19px;">${richTextToHtml(s.textContent)}</p>${
      s.email.trim()
        ? `<p style="margin:10px 0 0;"><a href="mailto:${escapeHtml(
            s.email
          )}" style="font-family:${font};color:${
            s.textColor
          };font-size:15px;text-decoration:underline;">${escapeHtml(
            s.email
          )}</a></p>`
        : ""
    }${
      s.phoneNumber.trim()
        ? `<p style="margin:6px 0 0;font-family:${font};color:${
            s.textColor
          };font-size:15px;">${escapeHtml(s.phoneNumber)}</p>`
        : ""
    }</div>`,
    background: s.backgroundColor,
    padding: "26px 36px",
    align: "left",
  });
};

export const renderSignature = (
  block: EmailBuilderBlockOf<"signature">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const capsLine = [s.designation.trim(), s.chamberName.trim()]
    .filter(Boolean)
    .join(", ");

  return sectionShell({
    inner: `<hr style="border:none;border-top:1px solid #111827;margin:0 0 22px;" /><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:16px;">Sincerely,</p><p style="margin:14px 0 0;font-family:'Snell Roundhand','Segoe Script','Brush Script MT',cursive;color:${
      s.textColor
    };font-size:34px;font-style:italic;">${richTextToHtml(
      s.addSignature
    )}</p><p style="margin:14px 0 0;font-family:${font};color:${
      s.textColor
    };font-size:16px;font-weight:800;">${richTextToHtml(s.name)}</p>${
      capsLine
        ? `<p style="margin:6px 0 0;font-family:${font};color:#6b7280;font-size:12px;letter-spacing:2px;text-transform:uppercase;">${escapeHtml(
            capsLine
          )}</p>`
        : ""
    }`,
    background: s.backgroundColor,
    padding: "30px 36px",
    align: "left",
  });
};

export const renderEmailBuilderBlock = (
  block: EmailBuilderBlock,
  theme: EmailBuilderRenderTheme
): string => {
  switch (block.type) {
    case "textAndButtonHeroBanner":
      return renderTextAndButtonHeroBanner(block, theme);
    case "textHeroBanner":
      return renderTextHeroBanner(block, theme);
    case "emailBanner":
      return renderEmailBanner(block, theme);
    case "updateBanner":
      return renderUpdateBanner(block, theme);
    case "alertBanner":
      return renderAlertBanner(block, theme);
    case "advocacyUpdateBanner":
      return renderAdvocacyUpdateBanner(block, theme);
    case "memberSpotlight":
      return renderMemberSpotlight(block, theme);
    case "articleList":
      return renderArticleList(block, theme);
    case "splitArticleWithBorder":
      return renderSplitArticleWithBorder(block, theme);
    case "textAndEmailContent":
      return renderTextAndEmailContent(block, theme);
    case "fourGridIconAndTitle":
      return renderFourGridIconAndTitle(block, theme);
    case "sixGridIconAndTitle":
      return renderSixGridIconAndTitle(block, theme);
    case "upcomingEventsList":
      return renderUpcomingEventsList(block, theme);
    case "eventAnnouncement":
      return renderEventAnnouncement(block, theme);
    case "specialGuestSponsor":
      return renderSpecialGuestSponsor(block, theme);
    case "sponsorsList":
      return renderSponsorsList(block, theme);
    case "advertisingPdf":
      return renderAdvertisingPdf(block, theme);
    case "keyLinks":
      return renderKeyLinks(block, theme);
    case "inquiries":
      return renderInquiries(block, theme);
    case "signature":
    default:
      return renderSignature(block as EmailBuilderBlockOf<"signature">, theme);
  }
};

const GREETING_TOKENS: Record<
  Exclude<EmailBuilderState["greetingLine"], "none">,
  string
> = {
  firstName: "{First Name}",
  fullName: "{First and Last Name}",
  companyName: "{Company Name}",
};

const renderHeader = (
  state: EmailBuilderState,
  theme: EmailBuilderRenderTheme
): string => {
  const logo = state.logoUrl
    ? `<div style="text-align:center;padding:26px 32px 10px;"><img src="${escapeHtml(
        state.logoUrl
      )}" alt="Chamber logo" width="200" style="display:inline-block;width:auto;max-width:200px;height:auto;max-height:100px;" /></div>`
    : "";
  const greeting =
    state.greetingLine === "none"
      ? ""
      : `<p style="margin:0;padding:22px 36px 0;font-family:${fontStack(
          theme.fontFamily
        )};color:${theme.textColor};font-size:17px;font-weight:700;text-align:left;">Hi ${
          GREETING_TOKENS[state.greetingLine]
        },</p>`;

  return `${logo}${greeting}`;
};

const renderFooterBlock = (
  state: EmailBuilderState,
  theme: EmailBuilderRenderTheme
): string => {
  const font = fontStack(theme.fontFamily);
  const iconCircle = (glyph: string): string =>
    `<td style="padding:0 7px;"><div style="width:44px;height:44px;line-height:44px;border-radius:999px;background:#1F2A3A;color:#ffffff;font-size:18px;text-align:center;">${glyph}</div></td>`;
  const footerLines = state.footerText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map(
      (line, index) =>
        `<p style="margin:${
          index === 0 ? "22px" : "10px"
        } 0 0;font-family:${font};color:#ffffff;font-size:${
          index === 0 ? "15px" : "13px"
        };line-height:1.6;">${escapeHtml(line)}</p>`
    )
    .join("");

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="background:${
    theme.footerBackgroundColor
  };padding:34px 36px;text-align:center;"><table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:0 auto;"><tr>${iconCircle(
    "&#8635;"
  )}${iconCircle("@")}${iconCircle(
    "&#128279;"
  )}</tr></table>${footerLines}<p style="margin:18px 0 0;"><a href="#" style="font-family:${font};color:${
    theme.highlightColor
  };font-size:14px;font-weight:700;text-decoration:underline;">Unsubscribe</a><span style="display:inline-block;width:1px;height:12px;background:#ffffff;margin:0 14px;vertical-align:middle;"></span><a href="#" style="font-family:${font};color:${
    theme.highlightColor
  };font-size:14px;font-weight:700;text-decoration:underline;">Update Preferences</a></p></td></tr></table>`;
};

const blockToTextLines = (block: EmailBuilderBlock): string[] => {
  switch (block.type) {
    case "textAndButtonHeroBanner":
      return [
        block.settings.title,
        block.settings.helperText,
        block.settings.buttonName,
      ];
    case "textHeroBanner":
      return [block.settings.title, block.settings.textContent];
    case "emailBanner":
      return [
        block.settings.buttonName,
        block.settings.title.replace(/\n/g, " "),
      ];
    case "updateBanner":
      return [
        block.settings.title,
        block.settings.description,
        block.settings.buttonName,
      ];
    case "alertBanner":
      return [block.settings.alertTitle];
    case "advocacyUpdateBanner":
      return [
        block.settings.title,
        block.settings.label,
        block.settings.description,
        block.settings.buttonName,
      ];
    case "memberSpotlight":
      return [
        block.settings.title,
        ...block.settings.items.flatMap((item) => [
          item.title,
          item.description,
        ]),
      ];
    case "articleList":
      return block.settings.items.flatMap((item) => [
        item.title,
        item.description,
      ]);
    case "splitArticleWithBorder":
      return block.settings.items.flatMap((item) => [item.title, item.desc]);
    case "textAndEmailContent":
      return [block.settings.textContent];
    case "fourGridIconAndTitle":
    case "sixGridIconAndTitle":
      return [
        block.settings.title,
        ...block.settings.items.map((item) => item.title),
      ];
    case "upcomingEventsList":
      return [
        block.settings.title,
        ...block.settings.events.flatMap((event) => [
          event.eventTitle,
          [event.date, event.time].filter(Boolean).join(" | "),
          event.location.replace(/\n/g, ", "),
        ]),
      ];
    case "eventAnnouncement":
      return [
        block.settings.title,
        ...block.settings.events.flatMap((event) => [
          event.eventTitle,
          event.date,
          event.time,
          event.location.replace(/\n/g, ", "),
          "Register Now",
        ]),
      ];
    case "specialGuestSponsor":
      return [block.settings.title, block.settings.textContent];
    case "sponsorsList":
      return [block.settings.title];
    case "advertisingPdf":
      return [block.settings.title, block.settings.buttonName];
    case "keyLinks":
      return [
        block.settings.title,
        ...block.settings.items.map((item) => item.buttonText),
      ];
    case "inquiries":
      return [
        block.settings.title,
        block.settings.textContent,
        block.settings.email,
        block.settings.phoneNumber,
      ];
    case "signature":
    default: {
      const settings = (block as EmailBuilderBlockOf<"signature">).settings;

      return [
        "Sincerely,",
        settings.name,
        [settings.designation, settings.chamberName].filter(Boolean).join(", "),
      ];
    }
  }
};

export const renderEmailBuilderState = (
  builderState: EmailBuilderState
): RenderedEmailBuilderContent => {
  const theme = getRenderTheme(builderState);
  const blocksHtml = builderState.blocks
    .map((block) => renderEmailBuilderBlock(block, theme))
    .join("");
  const bodyHtml = `<div style="background:#f8fafc;padding:24px 0 0;"><div style="max-width:680px;margin:0 auto;background:${theme.backgroundColor};border:1px solid #e5e7eb;border-bottom:none;">${renderHeader(
    builderState,
    theme
  )}${blocksHtml}</div></div>`;
  const footerHtml = `<div style="background:#f8fafc;padding:0 0 24px;"><div style="max-width:680px;margin:0 auto;border:1px solid #e5e7eb;border-top:none;">${renderFooterBlock(
    builderState,
    theme
  )}</div></div>`;
  const text = [
    builderState.greetingLine === "none"
      ? ""
      : `Hi ${GREETING_TOKENS[builderState.greetingLine]},`,
    ...builderState.blocks.flatMap(blockToTextLines).map(stripSimpleMarkup),
    builderState.footerText,
    "Unsubscribe | Update Preferences",
  ]
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n\n");

  return { html: bodyHtml + footerHtml, bodyHtml, footerHtml, text };
};

// ---------------------------------------------------------------------------
// Stored-state parsing (version 2 + version-1 migration)
// ---------------------------------------------------------------------------

type LegacyEmailBuilderGridItem = {
  id?: string;
  title?: string;
  body?: string;
  iconLabel?: string;
  imageUrl?: string;
};

type LegacyEmailBuilderBlock = {
  id?: string;
  type?: string;
  title?: string;
  body?: string;
  buttonLabel?: string;
  buttonUrl?: string;
  imageUrl?: string;
  iconLabel?: string;
  textColor?: string;
  highlightColor?: string;
  backgroundColor?: string;
  items?: LegacyEmailBuilderGridItem[];
};

const legacyIconToKey = (iconLabel?: string): EmailBuilderIconKey => {
  switch ((iconLabel || "").toLowerCase()) {
    case "calendar":
      return "calendar";
    case "megaphone":
      return "megaphone";
    case "star":
      return "sparkle";
    case "email":
      return "email";
    default:
      return "calendar";
  }
};

const legacyColorOverrides = (legacy: LegacyEmailBuilderBlock) => ({
  ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
  ...(legacy.highlightColor ? { highlightColor: legacy.highlightColor } : {}),
  ...(legacy.backgroundColor
    ? { backgroundColor: legacy.backgroundColor }
    : {}),
});

// Version-1 blocks map to the nearest canonical version-2 block, best-effort.
const migrateLegacyBlock = (
  legacy: LegacyEmailBuilderBlock
): EmailBuilderBlock | null => {
  const title = legacy.title || "";
  const body = legacy.body || "";
  const buttonLabel = legacy.buttonLabel || "";
  const buttonUrl = legacy.buttonUrl || "";
  const imageUrl = legacy.imageUrl || "";

  switch (legacy.type) {
    case "article":
      return createMemberSpotlightBlock({
        title: title || "Member Spotlight",
        items: [
          createMemberSpotlightItem({
            imageUrl,
            title,
            description: body,
            buttonName: buttonLabel || "Read Their Story",
            addLink: buttonUrl,
          }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "eventDisplay":
      return createUpcomingEventsListBlock({
        events: [
          createEventItem({ eventTitle: title, location: body, linkUrl: buttonUrl }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "eventDetails":
      return createEventAnnouncementBlock({
        events: [
          createEventItem({ eventTitle: title, location: body, linkUrl: buttonUrl }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "fourGridIconTitle":
      return createFourGridIconAndTitleBlock({
        title,
        items: (legacy.items || []).map((item) =>
          createIconGridItem({
            icon: legacyIconToKey(item.iconLabel),
            title: item.title || "",
          })
        ),
        ...legacyColorOverrides(legacy),
      });
    case "sixImageGrid":
      return createSixGridIconAndTitleBlock({
        title,
        items: (legacy.items || []).map((item) =>
          createIconGridItem({
            icon: legacyIconToKey(item.iconLabel),
            title: item.title || "",
          })
        ),
        ...legacyColorOverrides(legacy),
      });
    case "textButtonBanner":
    case "emailBanner":
      return createUpdateBannerBlock({
        title,
        description: body,
        buttonName: buttonLabel,
        addLink: buttonUrl,
        ...legacyColorOverrides(legacy),
      });
    case "splitArticleBorder":
      return createSplitArticleWithBorderBlock({
        items: [
          createSplitArticleItem({
            title,
            desc: body,
            buttonName: buttonLabel,
            addLink: buttonUrl,
          }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "imageTextBanner":
      return createEmailBannerBlock({
        title,
        ceoImageUrl: imageUrl,
        buttonName: buttonLabel,
        addLink: buttonUrl,
        ...legacyColorOverrides(legacy),
      });
    case "textBorder":
    case "textOnlyBanner":
      return createTextAndEmailContentBlock({
        textContent: [title, body].filter(Boolean).join("\n\n"),
        textAlign: "left",
        ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
        ...(legacy.backgroundColor
          ? { backgroundColor: legacy.backgroundColor }
          : {}),
      });
    case "sponsorLogo":
      return createSpecialGuestSponsorBlock({
        title: title || "SPECIAL GUEST SPONSOR",
        sponsorLogoUrl: imageUrl,
        textContent: body,
        ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
        ...(legacy.backgroundColor
          ? { backgroundColor: legacy.backgroundColor }
          : {}),
      });
    case "advertisingLogo":
      return createAdvertisingPdfBlock({
        title,
        imageUrl,
        buttonName: buttonLabel || "View Full PDF",
        addLink: buttonUrl,
        ...legacyColorOverrides(legacy),
      });
    case "signature":
      return createSignatureBlock({
        addSignature: body || title,
        name: body || title,
        designation: "",
        chamberName: "",
        ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
        ...(legacy.backgroundColor
          ? { backgroundColor: legacy.backgroundColor }
          : {}),
      });
    default:
      return null;
  }
};

const isKnownBlockType = (value: unknown): value is EmailBuilderBlockType =>
  typeof value === "string" && value in blockFactories;

// Fills any missing settings fields with factory defaults so older/partial
// version-2 payloads stay renderable.
const sanitizeStoredBlock = (raw: unknown): EmailBuilderBlock | null => {
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as { id?: unknown; type?: unknown; settings?: unknown };
  if (!isKnownBlockType(candidate.type)) return null;
  const fallback = blockFactories[candidate.type]();
  const storedSettings =
    candidate.settings && typeof candidate.settings === "object"
      ? (candidate.settings as Record<string, unknown>)
      : {};

  return {
    ...fallback,
    id: typeof candidate.id === "string" && candidate.id ? candidate.id : fallback.id,
    settings: { ...fallback.settings, ...storedSettings },
  } as EmailBuilderBlock;
};

export const parseEmailBuilderStateJson = ({
  builderStateJson,
  chamberName,
  fallbackContent,
}: {
  builderStateJson?: string | null;
  chamberName?: string;
  fallbackContent?: string;
}): EmailBuilderState => {
  const defaultState = buildDefaultEmailBuilderState(chamberName);

  if (builderStateJson) {
    try {
      const parsed = JSON.parse(builderStateJson) as {
        version?: number;
        blocks?: unknown[];
      } & Omit<Partial<EmailBuilderState>, "version" | "blocks">;

      if (parsed?.version === 2 && Array.isArray(parsed.blocks)) {
        const blocks = parsed.blocks
          .map(sanitizeStoredBlock)
          .filter(Boolean) as EmailBuilderBlock[];

        return {
          ...defaultState,
          ...parsed,
          version: 2,
          fontFamily: parsed.fontFamily || defaultState.fontFamily,
          theme: { ...defaultState.theme, ...(parsed.theme || {}) },
          blocks,
          selectedBlockId: blocks.some(
            (block) => block.id === parsed.selectedBlockId
          )
            ? (parsed.selectedBlockId as string)
            : "",
        };
      }

      if (parsed?.version === 1 && Array.isArray(parsed.blocks)) {
        const blocks = (parsed.blocks as LegacyEmailBuilderBlock[])
          .map(migrateLegacyBlock)
          .filter(Boolean) as EmailBuilderBlock[];

        return {
          ...defaultState,
          logoUrl: typeof parsed.logoUrl === "string" ? parsed.logoUrl : "",
          greetingLine: parsed.greetingLine || "none",
          footerText:
            typeof parsed.footerText === "string" && parsed.footerText.trim()
              ? parsed.footerText
              : defaultState.footerText,
          theme: { ...defaultState.theme, ...(parsed.theme || {}) },
          blocks,
          selectedBlockId: "",
        };
      }
    } catch {
      // Unreadable stored state falls back to generated starter content below.
    }
  }

  const state = defaultState;
  if (fallbackContent?.trim()) {
    state.blocks = [
      createTextAndEmailContentBlock({
        textContent: fallbackContent.trim(),
        textAlign: "left",
      }),
    ];
  }

  return state;
};

// ---------------------------------------------------------------------------
// Template presets (EMAIL-007 — verbatim seeded content from the template
// screenshots; gallery copy from the Step 3 gallery screenshots)
// ---------------------------------------------------------------------------

export type EmailTemplateKey =
  | "newsletter"
  | "eblast"
  | "advocacyUpdate"
  | "eventAnnouncement"
  | "mediaRelease"
  | "memberWelcome"
  | "fromTheDesk"
  | "customEmail";

export type EmailTemplatePreset = {
  key: EmailTemplateKey;
  /** Gallery card heading text (uppercase; card number comes from list order). */
  title: string;
  /** Title-case name for "Email Template: <name>" in the builder header. */
  builderTitle: string;
  description: string;
  /** Copy after the "Best For: " label. */
  bestFor: string;
  /** Copy after the "Design Tip: " label. */
  designTip: string;
  /** Fresh factory-built blocks on every access (safe to mutate). */
  readonly orderedBlocks: EmailBuilderBlock[];
};

const buildNewsletterTemplateBlocks = (): EmailBuilderBlock[] => [
  createTextAndButtonHeroBannerBlock(),
  createFourGridIconAndTitleBlock({
    title: "What is in the Newsletter?",
    items: [
      createIconGridItem({ icon: "calendar", title: "UPCOMING EVENTS" }),
      createIconGridItem({ icon: "megaphone", title: "CHAMBER NEWS" }),
      createIconGridItem({ icon: "memberSearch", title: "MEMBER SPOTLIGHT" }),
      createIconGridItem({ icon: "personAdd", title: "NEW MEMBERS" }),
      createIconGridItem({ icon: "sparkle", title: "PERKS" }),
    ],
  }),
  createUpcomingEventsListBlock(),
  createMemberSpotlightBlock(),
  createAdvocacyUpdateBannerBlock(),
  createSplitArticleWithBorderBlock(),
  createSpecialGuestSponsorBlock(),
  createSponsorsListBlock({ title: "OUR SPONSOR" }),
];

const buildEBlastTemplateBlocks = (): EmailBuilderBlock[] => [
  createAlertBannerBlock(),
  createUpdateBannerBlock(),
  createFourGridIconAndTitleBlock({
    title: "",
    items: [
      createIconGridItem({ icon: "bolt", title: "Quick & Easy Application" }),
      createIconGridItem({ icon: "dollar", title: "Up to $10,000 in Funding" }),
      createIconGridItem({ icon: "calendar", title: "Deadline June 30, 2025" }),
    ],
  }),
  createAdvertisingPdfBlock(),
];

const buildAdvocacyUpdateTemplateBlocks = (): EmailBuilderBlock[] => [
  createAlertBannerBlock({ alertIcon: "gavel", alertTitle: "ADVOCACY UPDATE" }),
  createTextHeroBannerBlock({
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    title: "Support Smart Growth in Our Community",
    textContent:
      "Proposed changes to municipal zoning could impact local businesses and future development. Your voice is essential in shaping the future of Sand Bay's economic landscape.",
  }),
  createArticleListBlock(),
  createUpdateBannerBlock({
    title: "Take Action",
    description:
      "Please take 2 minutes to complete our survey and let your voice be heard by the planning committee.",
    buttonName: "Take the Survey",
    backgroundColor: emailBuilderColors.lightBlue,
  }),
  createKeyLinksBlock(),
];

const buildEventAnnouncementTemplateBlocks = (): EmailBuilderBlock[] => [
  createEventAnnouncementBlock(),
  createTextAndEmailContentBlock(),
  createSpecialGuestSponsorBlock(),
];

const buildMediaReleaseTemplateBlocks = (): EmailBuilderBlock[] => [
  createAlertBannerBlock({ alertIcon: "megaphone", alertTitle: "MEDIA RELEASE" }),
  createTextHeroBannerBlock({
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    title: "Sand Bay Chamber Announces New Board of Directors",
    textContent: "Local business leaders committed to our community's future.",
  }),
  createTextAndEmailContentBlock({
    textAlign: "left",
    textContent:
      "Sand Bay, June 18, 2025 — The Sand Bay Chamber of Commerce is pleased to announce the appointment of its 2025–2026 Board of Directors. This distinguished group of leaders brings diverse expertise across retail, technology, and manufacturing sectors, ensuring the Chamber continues to serve as a catalyst for economic growth and regional prosperity.\n\nThe newly appointed board will focus on strategic initiatives including small business digital transformation, sustainable tourism development, and workforce advocacy. \"We are thrilled to welcome such a visionary team,\" said Karen Hattie, Executive Director.",
  }),
  createInquiriesBlock(),
];

const buildMemberWelcomeTemplateBlocks = (): EmailBuilderBlock[] => [
  createTextHeroBannerBlock(),
  createTextAndEmailContentBlock({
    textAlign: "left",
    textContent:
      "Hi {First Name},\n\nWelcome to the Sand Bay Chamber of Commerce! We're here to support your business and help you connect, grow, and thrive in our vibrant community.\n\nBelow you'll find a summary of the benefits included in your membership package.",
  }),
  createSixGridIconAndTitleBlock(),
  createUpdateBannerBlock({
    title: "",
    description: "",
    buttonName: "Explore Your Benefits",
  }),
];

const buildFromTheDeskTemplateBlocks = (): EmailBuilderBlock[] => [
  createEmailBannerBlock(),
  createTextAndEmailContentBlock({
    textAlign: "left",
    textContent:
      "Dear Members,\n\nI'm proud to share some exciting updates about our chamber and the positive impact we're making together. It has been a transformative quarter for our business community, and your continued engagement is what drives this momentum.\n\nOur focus remains on supporting your business, strengthening our community, and building a better future for all. From new networking initiatives to expanded advocacy at the municipal level, we are committed to being your most valuable partner in growth.\n\nThank you for being an essential part of the Sand Bay Chamber. We look forward to seeing you at our upcoming Town Hall next month.",
  }),
  createSignatureBlock(),
];

export const emailTemplatePresets: EmailTemplatePreset[] = [
  {
    key: "newsletter",
    title: "NEWSLETTER",
    builderTitle: "Newsletter",
    description: "Share updates, events, and member news.",
    bestFor:
      "Regular communication that provides value and keeps members informed.",
    designTip: "Use clear sections and mixed content for engagement.",
    get orderedBlocks() {
      return buildNewsletterTemplateBlocks();
    },
  },
  {
    key: "eblast",
    title: "E-BLAST",
    builderTitle: "E-Blast",
    description: "Short, urgent messages to drive action.",
    bestFor: "Time-sensitive updates that require quick action or awareness.",
    designTip: "Use a strong headline, contrasting colors, and a clear CTA.",
    get orderedBlocks() {
      return buildEBlastTemplateBlocks();
    },
  },
  {
    key: "advocacyUpdate",
    title: "ADVOCACY UPDATE",
    builderTitle: "Advocacy Update",
    description: "Inform members about key issues and actions.",
    bestFor: "Engaging members on important issues and encouraging advocacy.",
    designTip: "Clearly explain the issue and make the call-to-action easy.",
    get orderedBlocks() {
      return buildAdvocacyUpdateTemplateBlocks();
    },
  },
  {
    key: "eventAnnouncement",
    title: "EVENT ANNOUNCEMENT",
    builderTitle: "Event Announcement",
    description: "Promote upcoming events and drive registrations.",
    bestFor: "Driving registrations and attendance for events.",
    designTip: "Highlight key details and use a prominent CTA button.",
    get orderedBlocks() {
      return buildEventAnnouncementTemplateBlocks();
    },
  },
  {
    key: "mediaRelease",
    title: "MEDIA RELEASE",
    builderTitle: "Media Release",
    description: "Share official news and announcements.",
    bestFor: "Sharing official announcements with members and media.",
    designTip: "Keep it professional, clear, and easy to read.",
    get orderedBlocks() {
      return buildMediaReleaseTemplateBlocks();
    },
  },
  {
    key: "memberWelcome",
    title: "MEMBER WELCOME",
    builderTitle: "Member Welcome",
    description: "Welcome new members and introduce resources.",
    bestFor: "Onboarding new members and showcasing value.",
    designTip: "Personalize the greeting and highlight key benefits.",
    get orderedBlocks() {
      return buildMemberWelcomeTemplateBlocks();
    },
  },
  {
    key: "fromTheDesk",
    title: "FROM THE DESK",
    builderTitle: "From the Desk",
    description: "Personal messages from leadership.",
    bestFor: "Building trust and connection through leadership.",
    designTip: "Use a personal tone, photo, and authentic message.",
    get orderedBlocks() {
      return buildFromTheDeskTemplateBlocks();
    },
  },
  {
    key: "customEmail",
    title: "CUSTOM EMAIL",
    builderTitle: "Custom Email",
    description: "Create a message that fits your unique needs.",
    bestFor: "Unique messages or campaigns that don't fit into standard templates.",
    designTip: "Keep your message focused and align with your brand.",
    get orderedBlocks() {
      return [] as EmailBuilderBlock[];
    },
  },
];

export const getEmailTemplatePreset = (
  key: string
): EmailTemplatePreset | undefined =>
  emailTemplatePresets.find((preset) => preset.key === key);
